export type RemoveColumnTarget = {
  group: string
  column: string
  sectionKey?: string
}

export type RemoveColumnFailure = 'group-not-found' | 'column-not-found' | 'single-column'

export type RemoveColumnResult =
  | { ok: true; html: string }
  | { ok: false; reason: RemoveColumnFailure }

export type RemoveColumnOptions = {
  stylesHtml?: string
}

type CellRecord = {
  el: HTMLElement
  keyed: boolean
  key: string
  start: number
  span: number
}

type ReflowPlan = {
  display: 'flex' | 'grid'
  widthByElement: Map<Element, number>
} | null

function cssEscape(value: string): string {
  const scope = globalThis as { CSS?: { escape?: (value: string) => string } }
  return scope.CSS?.escape ? scope.CSS.escape(value) : value
}

function cellSpan(el: Element): number {
  const parsed = Number.parseInt(el.getAttribute('colspan') ?? '', 10)
  return Number.isInteger(parsed) && parsed > 1 ? parsed : 1
}

function isCellLike(el: Element): boolean {
  if (el.hasAttribute('data-edit-col') || el.tagName === 'TD' || el.tagName === 'TH') return true
  const tag = el.tagName.toLowerCase()
  if (tag === 'svg' || tag === 'script' || tag === 'style') return false
  return el.getAttribute('aria-hidden') !== 'true'
}

function rowCells(row: Element): CellRecord[] {
  const cells: CellRecord[] = []
  let cursor = 0
  for (const el of Array.from(row.children)) {
    if (!isCellLike(el)) continue
    const span = cellSpan(el)
    const key = el.getAttribute('data-edit-col') ?? ''
    cells.push({ el: el as HTMLElement, keyed: Boolean(key), key, start: cursor, span })
    cursor += span
  }
  return cells
}

function extractStyleCss(html: string): string {
  if (!html) return ''
  const blocks = html.match(/<style[^>]*>[\s\S]*?<\/style>/gi) ?? []
  return blocks.map(block => block.replace(/<\/?style[^>]*>/gi, '')).join('\n')
}

export function removeColumn(
  sectionHtml: string,
  target: RemoveColumnTarget,
  options: RemoveColumnOptions = {}
): RemoveColumnResult {
  if (!target.group || !target.column) {
    return { ok: false, reason: 'group-not-found' }
  }
  const container = document.createElement('div')
  container.innerHTML = sectionHtml

  const attr = `[data-edit-group="${cssEscape(target.group)}"]`
  let group = target.sectionKey
    ? container.querySelector(
        `.ara-section[data-section-key="${cssEscape(target.sectionKey)}"] ${attr}`
      )
    : null
  if (!group) group = container.querySelector(attr)
  if (!group) return { ok: false, reason: 'group-not-found' }

  const first = group.querySelector(`[data-edit-col="${cssEscape(target.column)}"]`)
  if (!first) return { ok: false, reason: 'column-not-found' }

  const headerRow = first.parentElement
  if (!headerRow) return { ok: false, reason: 'column-not-found' }
  const headerCells = rowCells(headerRow)
  const targetStart = headerCells.find(cell => cell.key === target.column)?.start
  if (targetStart === undefined) return { ok: false, reason: 'column-not-found' }

  const columnCount = headerCells.reduce((total, cell) => total + cell.span, 0)
  if (columnCount <= 1) return { ok: false, reason: 'single-column' }

  const reflowPlan = measureReflowPlan(container, group, headerRow, extractStyleCss(options.stylesHtml ?? ''))

  const rows = new Set<Element>()
  for (const cell of group.querySelectorAll('[data-edit-col]')) {
    if (cell.parentElement) rows.add(cell.parentElement)
  }

  let removed = false
  for (const row of rows) {
    for (const cell of rowCells(row)) {
      if (cell.keyed && cell.key === target.column && cell.span === 1) {
        cell.el.remove()
        removed = true
        continue
      }
      const covers = cell.start <= targetStart && targetStart < cell.start + cell.span
      if (!covers) continue
      if (!cell.keyed && cell.el.querySelector('[data-edit-col]')) continue
      if (cell.span > 1) {
        const nextSpan = cell.span - 1
        if (nextSpan <= 1) cell.el.removeAttribute('colspan')
        else cell.el.setAttribute('colspan', String(nextSpan))
        removed = true
      } else if (!cell.keyed || cell.key === target.column) {
        cell.el.remove()
        removed = true
      }
    }
  }
  if (!removed) return { ok: false, reason: 'column-not-found' }

  removeConnectors(group, target.column)
  renumberMarkers(group, headerRow)
  if (reflowPlan) applyReflow(group, headerRow, reflowPlan)

  return { ok: true, html: container.innerHTML }
}

function measureReflowPlan(
  container: HTMLDivElement,
  group: Element,
  headerRow: Element,
  styleCss: string
): ReflowPlan {
  if (group.querySelector('[data-edit-connects]')) return null
  const headCells = rowCells(headerRow)
  if (headCells.length < 2 || headCells.some(cell => cell.span > 1)) return null
  const body = container.ownerDocument?.body
  if (!body) return null

  const prevStyle = container.getAttribute('style')
  container.style.position = 'absolute'
  container.style.visibility = 'hidden'
  container.style.width = '1000px'
  const styleEl = styleCss ? container.ownerDocument.createElement('style') : null
  if (styleEl) styleEl.textContent = styleCss
  if (styleEl) container.prepend(styleEl)
  body.appendChild(container)
  try {
    const display = getComputedStyle(headerRow).display
    if (display !== 'flex' && display !== 'grid') return null
    const widthByElement = new Map<Element, number>()
    for (const cell of headCells) {
      const width = cell.el.getBoundingClientRect().width
      if (!Number.isFinite(width) || width <= 0) return null
      widthByElement.set(cell.el, width)
    }
    const rows = new Set<Element>()
    for (const cell of group.querySelectorAll('[data-edit-col]')) {
      if (cell.parentElement) rows.add(cell.parentElement)
    }
    for (const row of rows) {
      const cells = rowCells(row)
      if (cells.length !== headCells.length || cells.some(cell => cell.span > 1)) return null
      cells.forEach((cell, index) => {
        widthByElement.set(cell.el, widthByElement.get(headCells[index].el) ?? 0)
      })
    }
    return { display, widthByElement }
  } finally {
    if (container.parentElement === body) body.removeChild(container)
    if (prevStyle === null) container.removeAttribute('style')
    else container.setAttribute('style', prevStyle)
    styleEl?.remove()
  }
}

function applyReflow(group: Element, headerRow: Element, plan: NonNullable<ReflowPlan>): void {
  const survivors = rowCells(headerRow)
  const widths = survivors.map(cell => plan.widthByElement.get(cell.el) ?? 0)
  if (widths.some(width => width <= 0)) return

  group.setAttribute('data-reflow', '')

  if (plan.display === 'grid') {
    const template = widths.map(width => `${width.toFixed(2)}fr`).join(' ')
    ;(headerRow as HTMLElement).style.gridTemplateColumns = template
    for (const row of groupRows(group)) {
      if (row === headerRow || rowCells(row).length !== widths.length) continue
      ;(row as HTMLElement).style.gridTemplateColumns = template
    }
    return
  }
  survivors.forEach((cell, index) => {
    cell.el.style.flex = `${widths[index].toFixed(2)} 1 0`
  })
  for (const row of groupRows(group)) {
    if (row === headerRow) continue
    const cells = rowCells(row)
    if (cells.length !== widths.length) continue
    cells.forEach((cell, index) => {
      cell.el.style.flex = `${widths[index].toFixed(2)} 1 0`
    })
  }
}

function groupRows(group: Element): Set<Element> {
  const rows = new Set<Element>()
  for (const cell of group.querySelectorAll('[data-edit-col]')) {
    if (cell.parentElement) rows.add(cell.parentElement)
  }
  return rows
}

function removeConnectors(group: Element, column: string): void {
  for (const connector of group.querySelectorAll('[data-edit-connects]')) {
    const joined = (connector.getAttribute('data-edit-connects') ?? '').split(/\s+/)
    if (joined.includes(column)) connector.remove()
  }
}

function renumberMarkers(group: Element, headerRow: Element): void {
  const headerCells = rowCells(headerRow)
  const numeric = headerCells.length > 0 && headerCells.every(cell => /^\d+$/.test(cell.key))
  if (!numeric) return

  let index = 0
  for (const cell of rowCells(headerRow)) {
    if (cell.keyed) cell.el.setAttribute('data-edit-col', String(index))
    index += cell.span
  }
  for (const cell of group.querySelectorAll('[data-edit-col]')) {
    if (cell.parentElement === headerRow) continue
    const start = rowCells(cell.parentElement ?? group).find(
      candidate => candidate.el === cell
    )?.start
    if (start !== undefined) cell.setAttribute('data-edit-col', String(start))
  }
}
